import { useEffect, useState } from 'react';
import { publicTrustUrl, tenantTrustPreviewUrl, publicApiOrigin, usPublicTrustUrl } from '../config';
import type { PublicCount, PublicTrustLoadState, PublicTrustSnapshot } from '../types';

function optionalUrl(value: unknown): string | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  return typeof value === 'string' ? value : null;
}

function optionalStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

/** A public count: a number, null (1 to 24, below the display floor), or
 *  undefined when an older API did not send the field. */
function optionalCount(value: unknown): PublicCount | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === 'number' && value >= 0 ? value : undefined;
}

/**
 * Validate required fields; tolerate additive fields so the page never breaks
 * when the API ships new public-safe keys (forward-compatible client).
 */
export function parsePublicTrustSnapshot(payload: unknown): PublicTrustSnapshot | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;
  if (typeof record.enforcement_active !== 'boolean') {
    return null;
  }
  const checks24h = optionalCount(record.checks_24h);
  if (checks24h === undefined) {
    return null;
  }
  if (!Array.isArray(record.articles_enforced)) {
    return null;
  }
  return {
    enforcement_active: record.enforcement_active,
    checks_24h: checks24h,
    checks_30d: optionalCount(record.checks_30d),
    // Absent on payloads from an API older than the region-agnostic seal rule;
    // trustSeal reads that case the way that API labelled it.
    eu_checks_24h: optionalCount(record.eu_checks_24h),
    eu_checks_30d: optionalCount(record.eu_checks_30d),
    articles_enforced: record.articles_enforced.map(String),
    articles_screened: optionalStringArray(record.articles_screened),
    frameworks_screened: optionalStringArray(record.frameworks_screened),
    display_name:
      record.display_name === null || typeof record.display_name === 'string'
        ? (record.display_name as string | null)
        : null,
    website_url:
      record.website_url === null || typeof record.website_url === 'string'
        ? (record.website_url as string | null)
        : null,
    // Absent stays absent: the JSON preview shows what the endpoint returned,
    // never a field an older API did not send (CE-REVIEW-TRUST-013 F1).
    trust_json_url: optionalUrl(record.trust_json_url),
    badge_svg_url: optionalUrl(record.badge_svg_url),
  };
}

type LoadResult =
  | { kind: 'not_found' }
  | { kind: 'error' }
  | { kind: 'ready'; snapshot: PublicTrustSnapshot; preview: boolean; apiOrigin: string | null };

async function loadPublic(slug: string): Promise<LoadResult> {
  const firstUrl = publicTrustUrl(slug);
  const first = await fetch(firstUrl, { credentials: 'omit' });
  if (first.status === 404) {
    const next = usPublicTrustUrl(firstUrl, slug);
    if (next) {
      const second = await fetch(next, { credentials: 'omit' });
      if (second.ok) {
        const parsed = parsePublicTrustSnapshot(await second.json());
        return parsed
          ? { kind: 'ready', snapshot: parsed, preview: false, apiOrigin: publicApiOrigin(second.url) || publicApiOrigin(next) }
          : { kind: 'error' };
      }
    }
    return { kind: 'not_found' };
  }
  if (!first.ok) {
    return { kind: 'error' };
  }
  const parsed = parsePublicTrustSnapshot(await first.json());
  return parsed
    ? { kind: 'ready', snapshot: parsed, preview: false, apiOrigin: publicApiOrigin(first.url) }
    : { kind: 'error' };
}

/**
 * Preview: the signed-in tenant's own data, whether public or not. The API
 * picks the tenant from the Bearer token; the URL slug must match the
 * tenant's own slug or the page is not found (no previewing other slugs).
 * Any auth failure falls back to the public path so a stale cookie never
 * changes what a visitor would see.
 */
async function loadPreview(slug: string): Promise<LoadResult | null> {
  const response = await fetch(tenantTrustPreviewUrl(), {
    credentials: 'include',
  });
  if (!response.ok) {
    return null;
  }
  const body: unknown = await response.json();
  if (!body || typeof body !== 'object') {
    return null;
  }
  const record = body as Record<string, unknown>;
  // preview_slug is the saved public_slug, or the slug enabling would assign
  // when none is saved yet; either way it is this tenant's own address only.
  if (record.preview_slug !== slug) {
    return { kind: 'not_found' };
  }
  const parsed = parsePublicTrustSnapshot(record.snapshot);
  return parsed ? { kind: 'ready', snapshot: parsed, preview: true, apiOrigin: publicApiOrigin(response.url) } : { kind: 'error' };
}

export function usePublicTrust(slug: string | undefined, previewRequested = false) {
  const [loadState, setLoadState] = useState<PublicTrustLoadState>('loading');
  const [snapshot, setSnapshot] = useState<PublicTrustSnapshot | null>(null);
  const [preview, setPreview] = useState(false);
  const [apiOrigin, setApiOrigin] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoadState('error');
      setSnapshot(null);
      return;
    }

    let cancelled = false;
    setLoadState('loading');
    setSnapshot(null);
    setPreview(false);
    setApiOrigin(null);

    const load: Promise<LoadResult> = previewRequested
      ? loadPreview(slug).then((result) => result ?? loadPublic(slug))
      : loadPublic(slug);

    load
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.kind === 'not_found') {
          setLoadState('not_found');
          return;
        }
        if (result.kind === 'error') {
          setLoadState('error');
          return;
        }
        setSnapshot(result.snapshot);
        setPreview(result.preview);
        setApiOrigin(result.apiOrigin);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setLoadState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug, previewRequested]);

  return { loadState, snapshot, preview, apiOrigin };
}
